import { Smile } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseMetricLogCard } from "@/core/components/tracking";
import { updateMoodRating } from "@/lib/api";
import { useProductContext } from "@/contexts/ProductContext";

interface MoodLogCardProps {
  onComplete?: () => void;
  onDataUpdate?: () => void;
  isLoading?: boolean;
}

const getMoodEmoji = (value: number): string => {
  if (value <= 25) return "😔";
  if (value <= 50) return "😐";
  if (value <= 75) return "🙂";
  return "😊";
};

export function MoodLogCard({ onComplete, onDataUpdate, isLoading }: MoodLogCardProps) {
  const { t } = useTranslation();
  const { currentProduct } = useProductContext();

  return (
    <BaseMetricLogCard
      // Display
      icon={Smile}
      gradientClasses="from-violet-500 to-purple-600"
      borderHoverClasses="hover:border-violet-500/40"

      // Input type
      inputType="slider"
      sliderConfig={{
        min: 0,
        max: 100,
        step: 25,
        defaultValue: 50,
        showScale: true,
        scaleLabels: ["😔", "😐", "🙂", "😊"],
      }}

      // Data field
      fieldName="mood_rating"

      // Validation
      minValue={0}
      maxValue={100}

      // Pre-translated strings
      cardTitle={t('mindcotine:actions.logMood', 'Log Mood')}
      cardDescription={t('mindcotine:actions.moodHelp', 'Track your mood')}
      dialogTitle={t('mindcotine:actions.logMood', 'Log Mood')}
      dialogDescription={t('mindcotine:actions.moodHelp', 'Track your mood')}
      inputLabel={t('mindcotine:actions.moodFor', 'Mood for')}
      inputPlaceholder="50"
      notesPlaceholder={t('mindcotine:actions.notesPlaceholder', 'Add any notes...')}
      loggedTitle={t('mindcotine:actions.moodLogged', 'Mood Logged')}
      loggedDescription={t('mindcotine:actions.moodLoggedDescription', 'Your mood has been recorded.')}
      errorInvalidInput={t('common:errors.invalidInput', 'Invalid Input')}
      errorInvalidValue={t('common:errors.invalidNumber', 'Please enter a valid number.')}

      // Analytics
      analyticsEvent="Mood Logged"
      analyticsDataMapper={(value, date) => ({
        mood_value: value,
        mood_label: value <= 25 ? t('common.moodlogcard.sad', 'Sad') : value <= 50 ? t('common.moodlogcard.neutral', 'Neutral') : value <= 75 ? t('common.moodlogcard.good', 'Good') : t('common.moodlogcard.great', 'Great'),
        date,
      })}

      // Popup triggers
      popupTriggers={[
        { trigger: "mood_logged" },
        { 
          trigger: "low_mood", 
          condition: (value) => value <= 25,
          contextMapper: (value) => ({ rating: value })
        }
      ]}

      // Custom value display (emoji)
      valueDisplay={getMoodEmoji}

      // API function
      updateFunction={updateMoodRating}

      // Callbacks
      onComplete={onComplete}
      onDataUpdate={onDataUpdate}
      isLoading={isLoading}

      // Product context
      productId={currentProduct}
    />
  );
}
